import React, { useEffect, useRef, useState } from 'react';
import {
  getMailboxes,
  createMailbox,
  getMailbox,
  addMailboxMember,
  removeMailboxMember,
  getMailboxMessages,
  deleteMailboxMessage,
  sendAsMailbox,
} from '../services/mailboxService';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { lookupUsers, getAddressBook } from '../services/userService';
import { uploadFile, openFile, downloadFile } from '../services/fileService';
import './MailboxesPage.css';

export default function MailboxesPage() {
  const { user } = useAuth();
  const inboxLayoutRef = useRef(null);
  const composeFileRef = useRef(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [mailboxTab, setMailboxTab] = useState('inbox');
  const [activeMailbox, setActiveMailbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxThreads, setInboxThreads] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [selectedInboxThreadId, setSelectedInboxThreadId] = useState('');
  const [threadPaneWidth, setThreadPaneWidth] = useState(280);
  const [isResizingInboxPane, setIsResizingInboxPane] = useState(false);

  // Create mailbox form
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createAlias, setCreateAlias] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [spaces, setSpaces] = useState([]);
  const [createSpaceId, setCreateSpaceId] = useState('');
  const [createError, setCreateError] = useState('');

  // Add member form
  const [allUsers, setAllUsers] = useState([]);
  const [addUserId, setAddUserId] = useState('');
  const [addUserRole, setAddUserRole] = useState('agent');
  const [addCanSendAs, setAddCanSendAs] = useState(false);

  // Send As compose
  const [showCompose, setShowCompose] = useState(false);
  const [composeRecipientId, setComposeRecipientId] = useState('');
  const [composeToQuery, setComposeToQuery] = useState('');
  const [composeMatches, setComposeMatches] = useState([]);
  const [addressBook, setAddressBook] = useState([]);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeFiles, setComposeFiles] = useState([]);
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState('');
  const [composeSent, setComposeSent] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 980;
  });

  useEffect(() => {
    const onResize = () => setIsCompactLayout(window.innerWidth <= 980);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isResizingInboxPane || isCompactLayout) return undefined;

    const onMouseMove = (event) => {
      const container = inboxLayoutRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const nextWidth = event.clientX - rect.left;
      const minWidth = 240;
      const maxWidth = Math.max(360, rect.width - 320);
      const clamped = Math.min(maxWidth, Math.max(minWidth, nextWidth));
      setThreadPaneWidth(clamped);
    };

    const onMouseUp = () => setIsResizingInboxPane(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isResizingInboxPane, isCompactLayout]);

  const getUserId = (entry) => String(entry?.user?._id || entry?.user || '');
  const mailboxMemberIds = new Set((activeMailbox?.members || []).map((member) => getUserId(member)));
  const currentMember = (activeMailbox?.members || []).find((member) => getUserId(member) === String(user?._id));
  const canManageMembers = currentMember?.role === 'admin';
  const canSendAs = Boolean(currentMember?.canSendAs);
  const canDeleteAnyMailboxMessage = currentMember?.role === 'admin';

  const syncMailboxInList = (updatedMailbox) => {
    setMailboxes((prev) => prev.map((mb) => (String(mb._id) === String(updatedMailbox._id) ? updatedMailbox : mb)));
  };

  const deriveThreadTitle = (thread) => {
    const participants = Array.isArray(thread?.participants) ? thread.participants : [];
    const external = participants.find((participant) => !mailboxMemberIds.has(String(participant?._id)));
    if (external?.name) return external.name;
    if (thread?.subject) return thread.subject;
    return 'Conversation';
  };

  const loadMailboxInbox = async (mailboxId, preferredThreadId = '') => {
    setInboxLoading(true);
    try {
      const res = await getMailboxMessages(mailboxId, preferredThreadId ? { threadId: preferredThreadId } : {});
      const nextMailbox = res?.data?.mailbox;
      const nextThreads = res?.data?.threads || [];
      const nextMessages = res?.data?.messages || [];
      const selectedId = res?.data?.selectedThreadId || '';

      if (nextMailbox) {
        setActiveMailbox(nextMailbox);
        syncMailboxInList(nextMailbox);
      }
      setInboxThreads(nextThreads);
      setInboxMessages(nextMessages);
      setSelectedInboxThreadId(selectedId);
    } catch (err) {
      console.error('getMailboxMessages failed:', err);
      setInboxThreads([]);
      setInboxMessages([]);
      setSelectedInboxThreadId('');
    } finally {
      setInboxLoading(false);
    }
  };

  // Load mailboxes and spaces/users for forms
  useEffect(() => {
    getMailboxes()
      .then((res) => setMailboxes(res.data.mailboxes || []))
      .catch((err) => console.error('getMailboxes failed:', err))
      .finally(() => setLoading(false));

    api.get('/spaces')
      .then((res) => {
        const list = res.data.spaces || [];
        setSpaces(list);
        if (list.length > 0) setCreateSpaceId(list[0]._id);
      })
      .catch(() => {});

    api.get('/users')
      .then((res) => setAllUsers(res.data.users || []))
      .catch(() => {});

    getAddressBook()
      .then((res) => setAddressBook(res.data.entries || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const query = composeToQuery.trim();
    if (!showCompose) return;
    if (composeRecipientId) return;
    if (!query) {
      setComposeMatches([]);
      setComposeRecipientId('');
      return;
    }

    let ignore = false;
    lookupUsers(query)
      .then((res) => {
        if (!ignore) setComposeMatches(res.data.users || []);
      })
      .catch(() => {
        if (!ignore) setComposeMatches([]);
      });

    return () => {
      ignore = true;
    };
  }, [composeToQuery, showCompose, composeRecipientId]);

  const openMailbox = async (mb) => {
    setActiveMailbox(mb);
    setMailboxTab('inbox');
    setDetailLoading(true);
    try {
      const res = await getMailbox(mb._id);
      const mailbox = res.data.mailbox;
      setActiveMailbox(mailbox);
      syncMailboxInList(mailbox);
      await loadMailboxInbox(mailbox._id);
    } catch (err) {
      console.error('getMailbox failed:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!createName.trim() || !createSpaceId) {
      setCreateError('Name and space are required.');
      return;
    }
    try {
      const res = await createMailbox({
        name: createName.trim(),
        emailAlias: createAlias.trim() || undefined,
        description: createDesc.trim(),
        spaceId: createSpaceId,
      });
      setMailboxes((prev) => [res.data.mailbox, ...prev]);
      setCreateName('');
      setCreateAlias('');
      setCreateDesc('');
      setShowCreate(false);
      await openMailbox(res.data.mailbox);
    } catch (err) {
      setCreateError(err?.response?.data?.message || 'Failed to create mailbox');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addUserId || !activeMailbox) return;
    try {
      const res = await addMailboxMember(activeMailbox._id, {
        userId: addUserId,
        role: addUserRole,
        canSendAs: addCanSendAs,
      });
      setActiveMailbox(res.data.mailbox);
      syncMailboxInList(res.data.mailbox);
      setAddUserId('');
      setAddCanSendAs(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add member');
    }
  };

  const existingMemberIds = new Set((activeMailbox?.members || []).map((m) => String(m.user?._id || m.user)));
  const availableUsers = allUsers.filter((u) => !existingMemberIds.has(String(u._id)));

  const recentRecipients = addressBook
    .map((entry) => entry.user)
    .filter((entry) => String(entry?._id) !== String(user?._id));

  const selectRecipient = (recipient) => {
    setComposeRecipientId(String(recipient._id));
    setComposeToQuery(recipient.name || recipient.email || '');
    setComposeMatches([]);
  };

  const handleSendAs = async (e) => {
    e.preventDefault();
    if (!composeRecipientId || !composeSubject.trim() || (!composeBody.trim() && composeFiles.length === 0)) return;
    setComposeSending(true);
    setComposeError('');
    try {
      const uploadedAttachments = [];
      for (const file of composeFiles) {
        const fileRes = await uploadFile(null, file, null, [composeRecipientId]);
        uploadedAttachments.push({
          fileRef: fileRes.data.file._id,
          name: fileRes.data.file.originalName || file.name,
        });
      }

      await sendAsMailbox(activeMailbox._id, {
        recipientId: composeRecipientId,
        subject: composeSubject.trim(),
        body: composeBody.trim(),
        attachments: uploadedAttachments,
      });
      const addressBookRes = await getAddressBook();
      setAddressBook(addressBookRes.data.entries || []);
      setComposeRecipientId('');
      setComposeToQuery('');
      setComposeMatches([]);
      setComposeSubject('');
      setComposeBody('');
      setComposeFiles([]);
      setShowCompose(false);
      setComposeSent(true);
      setTimeout(() => setComposeSent(false), 3000);
      await loadMailboxInbox(activeMailbox._id);
    } catch (err) {
      setComposeError(err?.response?.data?.message || 'Failed to send');
    } finally {
      setComposeSending(false);
    }
  };

  const handleInboxThreadSelect = async (threadId) => {
    if (!activeMailbox || !threadId) return;
    await loadMailboxInbox(activeMailbox._id, threadId);
  };

  const handleDeleteInboxMessage = async (messageId) => {
    if (!activeMailbox || !messageId) return;
    if (!window.confirm('Delete this message?')) return;
    try {
      await deleteMailboxMessage(activeMailbox._id, messageId);
      await loadMailboxInbox(activeMailbox._id, selectedInboxThreadId);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete message');
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    if (!activeMailbox || !memberUserId) return;
    if (!window.confirm('Remove this mailbox member?')) return;

    try {
      const res = await removeMailboxMember(activeMailbox._id, memberUserId);
      setActiveMailbox(res.data.mailbox);
      syncMailboxInList(res.data.mailbox);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove member');
    }
  };

  return (
    <div className="mailboxes-page">
      {/* ── Left panel: mailbox list ──────────────────────────────────────── */}
      <div className="mb-list-panel">
        <div className="mb-list-header">
          <span>Shared Mailboxes</span>
          <button className="btn-icon" onClick={() => setShowCreate((v) => !v)} title="New mailbox">
            +
          </button>
        </div>

        {showCreate && (
          <div className="mb-create-form-wrap">
            <form onSubmit={handleCreate} className="mb-create-form">
              <label className="form-label">Name *</label>
              <input
                className="form-input"
                placeholder="e.g. Support Team"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                autoFocus
              />
              <label className="form-label">Space *</label>
              <select
                className="form-input"
                value={createSpaceId}
                onChange={(e) => setCreateSpaceId(e.target.value)}
              >
                <option value="">Select a space...</option>
                {spaces.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
              <label className="form-label">Email alias (optional)</label>
              <input
                className="form-input"
                placeholder="support@example.com"
                value={createAlias}
                onChange={(e) => setCreateAlias(e.target.value)}
              />
              <label className="form-label">Description</label>
              <input
                className="form-input"
                placeholder="What this mailbox is for..."
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
              />
              {createError && <p className="form-error">{createError}</p>}
              <div className="mb-create-actions">
                <button type="submit" className="btn btn-primary btn-sm" disabled={!createName.trim() || !createSpaceId}>
                  Create
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <p className="panel-muted">Loading...</p>
        ) : mailboxes.length === 0 ? (
          <p className="panel-muted">No mailboxes yet.</p>
        ) : (
          <ul className="mb-list">
            {mailboxes.map((mb) => (
              <li
                key={mb._id}
                className={`mb-item ${activeMailbox?._id === mb._id ? 'active' : ''}`}
                onClick={() => openMailbox(mb)}
              >
                <div className="mb-item-name">{mb.name}</div>
                {mb.emailAlias && <div className="mb-item-alias">{mb.emailAlias}</div>}
                <div className="mb-item-meta">{mb.members?.length || 0} members</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Right panel: mailbox detail ───────────────────────────────────── */}
      <div className="mb-detail-panel">
        {!activeMailbox ? (
          <div className="mb-detail-empty">Select a mailbox to view details</div>
        ) : detailLoading ? (
          <div className="mb-detail-empty">Loading...</div>
        ) : (
          <>
            <div className="mb-detail-header">
              <div>
                <h2 className="mb-detail-name">{activeMailbox.name}</h2>
                {activeMailbox.emailAlias && (
                  <span className="mb-detail-alias">{activeMailbox.emailAlias}</span>
                )}
              </div>
              <span className={`mb-status-badge ${activeMailbox.isActive ? 'active' : 'inactive'}`}>
                {activeMailbox.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {activeMailbox.description && (
              <p className="mb-detail-desc">{activeMailbox.description}</p>
            )}

            <div className="mb-tab-row" role="tablist" aria-label="Mailbox tabs">
              <button
                type="button"
                className={`mb-tab-btn ${mailboxTab === 'inbox' ? 'active' : ''}`}
                onClick={() => setMailboxTab('inbox')}
              >
                Inbox
              </button>
              <button
                type="button"
                className={`mb-tab-btn ${mailboxTab === 'members' ? 'active' : ''}`}
                onClick={() => setMailboxTab('members')}
              >
                Members
              </button>
            </div>

            {mailboxTab === 'inbox' && (
              <>
                <div className="mb-inbox-layout" ref={inboxLayoutRef}>
                  <div
                    className="mb-inbox-threads-panel"
                    style={isCompactLayout ? undefined : { width: `${threadPaneWidth}px` }}
                  >
                    <h3 className="mb-section-heading">Conversations</h3>
                    {inboxLoading ? (
                      <p className="panel-muted">Loading inbox...</p>
                    ) : inboxThreads.length === 0 ? (
                      <div className="mb-inbox-empty-block">
                        <p className="panel-muted">No mailbox conversations yet.</p>
                        <p className="mb-inbox-hint">Delete appears on your messages, or on all messages if you are a mailbox admin.</p>
                      </div>
                    ) : (
                      <ul className="mb-inbox-thread-list">
                        {inboxThreads.map((thread) => (
                          <li
                            key={thread._id}
                            className={`mb-inbox-thread-item ${String(selectedInboxThreadId) === String(thread._id) ? 'active' : ''}`}
                            onClick={() => handleInboxThreadSelect(thread._id)}
                          >
                            <span className="mb-inbox-thread-title">{deriveThreadTitle(thread)}</span>
                            <span className="mb-inbox-thread-meta">
                              {thread.lastMessageAt ? new Date(thread.lastMessageAt).toLocaleString() : 'No activity'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {!isCompactLayout && (
                    <div
                      className={`mb-inbox-splitter ${isResizingInboxPane ? 'active' : ''}`}
                      onMouseDown={() => setIsResizingInboxPane(true)}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="Resize mailbox inbox panels"
                    />
                  )}

                  <div className="mb-inbox-messages-panel">
                    <h3 className="mb-section-heading">Messages</h3>
                    {inboxLoading ? (
                      <p className="panel-muted">Loading messages...</p>
                    ) : inboxMessages.length === 0 ? (
                      <div className="mb-inbox-empty-block">
                        <p className="panel-muted">Select a conversation to view messages.</p>
                        <p className="mb-inbox-hint">Delete appears on your messages, or on all messages if you are a mailbox admin.</p>
                      </div>
                    ) : (
                      <ul className="mb-inbox-message-list">
                        {inboxMessages.map((message) => {
                          const messageId = String(message._id);
                          const isMine = String(message.sender?._id) === String(user?._id);
                          const canDelete = isMine || canDeleteAnyMailboxMessage;
                          const senderName = message.sender?.name || 'Unknown';
                          return (
                            <li key={messageId} className={`mb-inbox-message-item ${isMine ? 'mine' : 'theirs'}`}>
                              <div className="mb-inbox-message-header">
                                <strong>{senderName}</strong>
                                <span>{message.createdAt ? new Date(message.createdAt).toLocaleString() : ''}</span>
                              </div>
                              <p className="mb-inbox-message-body">
                                {message.body || ''}
                              </p>
                              {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                                <div className="mb-inbox-attachments">
                                  {message.attachments.map((attachment, idx) => {
                                    const fileId = attachment.fileRef?._id || attachment.fileRef;
                                    const fileName = attachment.name || attachment.fileRef?.originalName || `Attachment ${idx + 1}`;
                                    return (
                                      <div className="mb-inbox-attachment-item" key={fileId || `${messageId}-att-${idx}`}>
                                        <button
                                          type="button"
                                          className="mb-inbox-attachment-link"
                                          onClick={() => openFile(fileId).catch(() => alert('Unable to open attachment'))}
                                        >
                                          📎 {fileName}
                                        </button>
                                        <button
                                          type="button"
                                          className="mb-inbox-attachment-download"
                                          onClick={() => downloadFile(fileId, fileName)}
                                        >
                                          ⬇
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {canDelete && (
                                <button
                                  type="button"
                                  className="mb-msg-delete-btn"
                                  onClick={() => handleDeleteInboxMessage(messageId)}
                                >
                                  Delete
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                {canSendAs && (
                  <div className="mb-compose-section">
                    <div className="mb-compose-header">
                      <h3 className="mb-section-heading">Send As {activeMailbox.name}</h3>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => { setShowCompose((v) => !v); setComposeError(''); }}
                      >
                        {showCompose ? 'Cancel' : 'Compose'}
                      </button>
                    </div>
                    {composeSent && <p className="mb-compose-success">Message sent!</p>}
                    {showCompose && (
                      <form onSubmit={handleSendAs} className="mb-compose-form">
                        <input
                          className="form-input"
                          placeholder="To: type a user name or email..."
                          value={composeToQuery}
                          onChange={(e) => {
                            setComposeToQuery(e.target.value);
                            setComposeRecipientId('');
                          }}
                        />
                        {recentRecipients.length > 0 && !composeToQuery.trim() && (
                          <div className="mb-recents-wrap">
                            <span className="mb-recents-label">Address Book</span>
                            <div className="mb-recents-list">
                              {recentRecipients.slice(0, 6).map((recipient) => (
                                <button
                                  key={recipient._id}
                                  type="button"
                                  className="mb-recipient-chip"
                                  onClick={() => selectRecipient(recipient)}
                                >
                                  {recipient.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {composeToQuery.trim() && composeMatches.length > 0 && (
                          <ul className="mb-recipient-results">
                            {composeMatches.map((recipient) => (
                              <li key={recipient._id}>
                                <button
                                  type="button"
                                  className="mb-recipient-result"
                                  onClick={() => selectRecipient(recipient)}
                                >
                                  <span>{recipient.name}</span>
                                  <span className="mb-recipient-result-email">{recipient.email}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {composeToQuery.trim() && composeMatches.length === 0 && !composeRecipientId && (
                          <p className="form-error">No user found.</p>
                        )}
                        <input
                          className="form-input"
                          placeholder="Subject..."
                          value={composeSubject}
                          onChange={(e) => setComposeSubject(e.target.value)}
                          autoFocus
                        />
                        <textarea
                          className="form-input mb-compose-body"
                          placeholder={`Write a message as ${activeMailbox.name}...`}
                          value={composeBody}
                          onChange={(e) => setComposeBody(e.target.value)}
                          rows={5}
                        />
                        <div className="mb-compose-attach-bar">
                          <button
                            type="button"
                            className="mb-attach-trigger"
                            onClick={() => composeFileRef.current?.click()}
                          >
                            +
                          </button>
                          <span className="mb-attach-label">Add files</span>
                        </div>
                        <input
                          ref={composeFileRef}
                          type="file"
                          multiple
                          className="mb-compose-file-input"
                          onChange={(e) => {
                            const next = Array.from(e.target.files || []);
                            if (next.length === 0) return;
                            setComposeFiles((prev) => [...prev, ...next]);
                            e.target.value = '';
                          }}
                        />
                        {composeFiles.length > 0 && (
                          <ul className="mb-compose-files">
                            {composeFiles.map((file, idx) => (
                              <li key={`${file.name}-${idx}`}>
                                <span>{file.name}</span>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => setComposeFiles((prev) => prev.filter((_, i) => i !== idx))}
                                >
                                  Remove
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {composeError && <p className="form-error">{composeError}</p>}
                        <div className="mb-compose-actions">
                          <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            disabled={composeSending || !composeRecipientId || !composeSubject.trim() || (!composeBody.trim() && composeFiles.length === 0)}
                          >
                            {composeSending ? 'Sending...' : `Send as ${activeMailbox.name}`}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </>
            )}

            {mailboxTab === 'members' && (
              <>
                <div className="mb-members-section">
                  <h3 className="mb-section-heading">Members</h3>
                  {(activeMailbox.members || []).length === 0 ? (
                    <p className="panel-muted">No members yet.</p>
                  ) : (
                    <ul className="mb-members-list">
                      {activeMailbox.members.map((m, idx) => {
                        const memberId = m.user?._id ? String(m.user._id) : '';
                        const isSelf = String(memberId) === String(user?._id);
                        return (
                          <li key={memberId || `mbmember-${idx}`} className="mb-member-item">
                            <div className="mb-member-avatar">
                              {(m.user?.name || '?')[0].toUpperCase()}
                            </div>
                            <div className="mb-member-info">
                              <span className="mb-member-name">{m.user?.name || 'Unknown'}</span>
                              <span className="mb-member-email">{m.user?.email}</span>
                            </div>
                            <div className="mb-member-badges">
                              <span className={`mb-role-badge role-${m.role}`}>{m.role}</span>
                              {m.canSendAs && <span className="mb-sendasbadge">Can Send As</span>}
                              {canManageMembers && memberId && (
                                <button
                                  type="button"
                                  className="mb-member-remove-btn"
                                  onClick={() => handleRemoveMember(memberId)}
                                  disabled={isSelf}
                                  title={isSelf ? 'Cannot remove yourself' : 'Remove member'}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {canManageMembers && availableUsers.length > 0 && (
                  <div className="mb-add-member-section">
                    <h3 className="mb-section-heading">Add Member</h3>
                    <form onSubmit={handleAddMember} className="mb-add-member-row">
                      <select
                        className="form-select"
                        value={addUserId}
                        onChange={(e) => setAddUserId(e.target.value)}
                      >
                        <option value="">Select a user...</option>
                        {availableUsers.map((u) => (
                          <option key={u._id} value={u._id}>{u.name} - {u.email}</option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm"
                        value={addUserRole}
                        onChange={(e) => setAddUserRole(e.target.value)}
                      >
                        <option value="agent">Agent</option>
                        <option value="admin">Admin</option>
                      </select>
                      <label className="mb-sendaslabel">
                        <input
                          type="checkbox"
                          checked={addCanSendAs}
                          onChange={(e) => setAddCanSendAs(e.target.checked)}
                        />
                        Can Send As
                      </label>
                      <button type="submit" className="btn btn-primary btn-sm" disabled={!addUserId}>
                        Add
                      </button>
                    </form>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
